import { ClientSavedTicket, ContactMessageStatus } from '@j-s/dr-shared';

const STORAGE_KEY = 'dr_client_tickets';

const listeners = new Set<() => void>();
let cachedTickets: ClientSavedTicket[] = [];
let isInitialized = false;

function notify(): void {
  listeners.forEach((l) => l());
}

function loadTicketsFromStorage(): ClientSavedTicket[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn('Failed to read tickets from localStorage:', err);

    return [];
  }
}

export function resetStorageCache(): void {
  cachedTickets = [];
  isInitialized = false;
}

export function getSavedTickets(): ClientSavedTicket[] {
  if (typeof window === 'undefined') return [];

  if (!isInitialized) {
    cachedTickets = loadTicketsFromStorage();
    isInitialized = true;
  }

  return cachedTickets;
}

export function subscribeTicketStorage(callback: () => void): () => void {
  listeners.add(callback);

  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      cachedTickets = loadTicketsFromStorage();
      callback();
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }

  return () => {
    listeners.delete(callback);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
}

export function saveTicketToStorage(ticket: ClientSavedTicket): void {
  if (typeof window === 'undefined') return;

  try {
    const current = getSavedTickets();
    const existingIndex = current.findIndex((t) => t.id === ticket.id);

    let updated: ClientSavedTicket[];

    if (existingIndex >= 0) {
      updated = [...current];
      updated[existingIndex] = ticket;
    } else {
      updated = [ticket, ...current];
    }

    cachedTickets = updated.slice(0, 50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedTickets));
    notify();
  } catch (err) {
    console.warn('Failed to save ticket to localStorage:', err);
  }
}

export function updateSavedTicketStatus(
  id: string,
  status: ContactMessageStatus
): void {
  if (typeof window === 'undefined') return;

  try {
    const current = getSavedTickets();
    const target = current.find((t) => t.id === id);

    if (target && target.lastStatus !== status) {
      target.lastStatus = status;
      cachedTickets = [...current];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedTickets));
      notify();
    }
  } catch (err) {
    console.warn('Failed to update ticket status in localStorage:', err);
  }
}

export function removeSavedTicket(id: string): void {
  if (typeof window === 'undefined') return;

  try {
    const current = getSavedTickets();

    cachedTickets = current.filter((t) => t.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedTickets));
    notify();
  } catch (err) {
    console.warn('Failed to remove ticket from localStorage:', err);
  }
}
